/**
 * Purpose: locate each distinct drawing on an uploaded sheet set so the
 *          pipeline can store coordinates on one raster per page.
 *
 * Input:  file attachment (PDF or raster image) + original filename + optional
 *         classifier building ids. Swedish construction drawings.
 * Output: structured output
 *         ({@link buildDrawingDetectionRootJsonSchema}).
 * Consumers:
 *   - `server/src/services/project-file-batches/project-file-drawing-crops.ts`
 *     writes each drawing as coordinates on that file's page raster.
 *   - Admin extracted-drawings modal groups rows by building / family / storey.
 *
 * Schema is source of truth for field shapes and null rules.
 */

import { PROJECT_FILE_DRAWING_DETECTION_SCHEMA_NAME } from './project-file-drawing-detection-json-schema.helpers';
import { formatProjectFilePageList } from './project-file-page-excerpt.helpers';

export { PROJECT_FILE_DRAWING_DETECTION_SCHEMA_NAME };

export type DrawingDetectionBuildingHint = {
  buildingInstanceId: string;
  buildingLabel: string | null;
};

export type DrawingDetectionBlueprintHint = {
  blueprintSubtype: string | null;
  pageHint: string | null;
  /** 1-based pages of the file; absent when the classifier gave none. */
  pages?: readonly number[];
};

/**
 * Every classifier id for the file, singular and list. The label is the one
 * the classifier already stored on that segment. Ids are not read out of a title.
 */
export const drawingDetectionBuildingHints = (
  rows: readonly {
    buildingInstanceId?: string | null;
    buildingInstanceIds?: readonly (string | null)[] | null;
    buildingLabel?: string | null;
  }[],
): DrawingDetectionBuildingHint[] => {
  const seen = new Set<string>();
  const hints: DrawingDetectionBuildingHint[] = [];
  for (const row of rows) {
    const ids = [
      ...(row.buildingInstanceIds ?? []),
      row.buildingInstanceId ?? null,
    ];
    for (const raw of ids) {
      const id = raw?.trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      hints.push({
        buildingInstanceId: id,
        buildingLabel: row.buildingLabel ?? null,
      });
    }
  }
  return hints;
};

/** Blueprint segments the classifier already stored — narrows which pages must yield crops. */
export const drawingDetectionBlueprintHints = (
  rows: readonly {
    type: string;
    blueprintSubtype?: string | null;
    pageHint?: string | null;
    pages?: readonly number[];
  }[],
): DrawingDetectionBlueprintHint[] =>
  rows
    .filter((row) => row.type === 'blueprint')
    .map((row) => ({
      blueprintSubtype: row.blueprintSubtype ?? null,
      pageHint: row.pageHint?.trim() || null,
      pages: row.pages,
    }));

/**
 * Cross-field instructions the JSON schema cannot encode.
 */
export const buildProjectFileDrawingDetectionInstructionBody = (): string =>
  [
    'You locate every distinct architectural drawing in this Swedish construction file so a downstream cropper can cut them out.',
    '',
    'Emit one `drawings` row per drawing. A sheet that shows one plan, section, or elevation emits one row for that drawing, even when the stamp is large or the drawing fills the page. A page with two drawings side by side is two rows. Zero rows only when the page has no plan, section, or elevation geometry.',
    '',
    'Bounding boxes:',
    '- Snug around this drawing only — geometry, its own dimensions, and its local title strip.',
    '- Never include a neighbouring drawing. A sliver of empty paper is better than a neighbour.',
    '- Use 0–1 fractions of the page. For an image file, `page` is always 1.',
    '',
    'Identity:',
    '- When classifier building hints are listed below, REUSE those ids. A drawing that shows several of them lists each id in `building_instance_ids`. A drawing whose title prints it for several buildings, as a list or a range of them, lists each of those ids, also when it draws one of them as the example. A drawing with no named building leaves that array empty. Do not invent a slug from the project name.',
    '- `drawing_kind`: plan for entréplan/normalplan/plan N/källare/garageplan/takplan/gårdsplan; section for sektion; elevation for fasad; other for details and site insets.',
    '- `storey_kind`: `typical` is an intermediate floor, including a numbered plan that has its own sheet and is not the entrance, not the uppermost floor, and not below ground. That role does not mean the sheet covers other floors. `entrance` is the entrance level (entréplan, bottenplan, or the plan directly above a garage or cellar). `top` is only the uppermost floor. A printed plan number is not `top` unless that floor is the top floor, and a plan number is not the entrance. `unknown` is a section, an elevation, or a plan whose role the sheet does not support. Do not invent a plan number the page does not print; a number in the file name is not one. A plan that shows several parts of one building lists every part id in `building_instance_ids`.',
    "- `storey_kind_per_building`: a plan that shows several buildings can show a different level of each, for example one building's courtyard level and another building's entrance level, when the site slopes or a podium joins them. Then give one row per building id with that building's role, and set `storey_kind` to the role the sheet names first. Empty when the role is the same for every building.",
    '- When the stamp prints a paper size, `scale` includes it, whether it sits in the scale parenthesis or in the stamp format field. Do not read the paper size from the file name. Null when the sheet prints no scale.',
  ].join('\n');

const buildFilenameBlock = (filename: string): string => {
  const safeFilename = JSON.stringify(filename.replace(/\r?\n/g, ' '));
  return `Original file name: ${safeFilename}`;
};

const buildBlueprintHintsBlock = (
  hints: readonly DrawingDetectionBlueprintHint[],
): string => {
  if (hints.length === 0) return '';
  const lines = hints.map((hint) => {
    const subtype = hint.blueprintSubtype?.trim() || 'blueprint';
    const pages =
      hint.pages != null && hint.pages.length > 0
        ? formatProjectFilePageList(hint.pages)
        : hint.pageHint?.trim() || '(page range not specified)';
    return `- ${subtype}, pages ${pages}`;
  });
  return [
    'Classifier blueprint segments (these page ranges contain plan, section, or elevation sheets — locate every distinct drawing on them):',
    ...lines,
    'When these segments exist, `drawings` must list every distinct plan, section, and elevation on those pages. An empty array is only valid if those pages truly have no drawable geometry.',
  ].join('\n');
};

const buildBuildingHintsBlock = (
  hints: readonly DrawingDetectionBuildingHint[],
): string => {
  if (hints.length === 0) {
    return 'Classifier building hints: none. Leave `building_instance_ids` empty. Do not invent a slug from the project name or from a word in the title.';
  }
  const lines = hints.map((h) => {
    const label = h.buildingLabel?.trim();
    return label
      ? `- ${h.buildingInstanceId} (${label})`
      : `- ${h.buildingInstanceId}`;
  });
  return ['Classifier building hints (reuse these ids):', ...lines].join('\n');
};

/** Runtime: filename + classifier building hints prepended to the instruction. */
export const projectFileDrawingDetectionUserText = (input: {
  fileName: string;
  buildingHints: readonly DrawingDetectionBuildingHint[];
  blueprintHints?: readonly DrawingDetectionBlueprintHint[];
}): string => {
  const blueprintBlock = buildBlueprintHintsBlock(input.blueprintHints ?? []);
  return [
    buildFilenameBlock(input.fileName),
    '',
    buildBuildingHintsBlock(input.buildingHints),
    ...(blueprintBlock === '' ? [] : ['', blueprintBlock]),
    '',
    buildProjectFileDrawingDetectionInstructionBody(),
  ].join('\n');
};

/** Second-pass nudge when the model returned zero rows despite blueprint classification. */
export const projectFileDrawingDetectionEmptyRetrySuffix = (): string =>
  [
    'Your previous response had an empty `drawings` array.',
    'The classifier already identified blueprint segments on this file (see the blueprint segment list above).',
    'Re-read the attachment and emit one `drawings` row per distinct plan, section, or elevation on those pages.',
    'Use snug bounding boxes; do not return an empty array unless those pages truly have no plan, section, or elevation geometry.',
  ].join(' ');

/** Catalog: same skeleton with placeholders. */
export const projectFileDrawingDetectionUserTextTemplate = (): string =>
  projectFileDrawingDetectionUserText({
    fileName: '{{originalFilename}}',
    buildingHints: [
      {
        buildingInstanceId: '{{buildingInstanceId}}',
        buildingLabel: '{{buildingLabel}}',
      },
    ],
    blueprintHints: [
      {
        blueprintSubtype: '{{blueprintSubtype}}',
        pageHint: '{{pageHint}}',
      },
    ],
  });
